import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { getDocumentTypesEndpointIdentifier } from './use-get-document-types-api';

const endpoint = '/ums/v1/cms/document-vault/types';

interface BodyType {
  name: string;
  category: string;
  isRequired?: boolean;
  hasExpiry?: boolean;
  sortOrder?: number;
}

type TData = Record<string, any>;
type TError = AxiosError;
type TVariables = { body: BodyType };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useCreateDocumentTypeApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);
      toast({ description: 'Document type created successfully', status: 'success' });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: endpoint,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getDocumentTypesEndpointIdentifier]);
      if (userOnSuccess) userOnSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
